import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import StatusBadge from "../components/StatusBadge";
import { getRequests, getGems, getUsers, getNotifications } from "../services/api";
import { Gem, FilePlus2, Cake, PartyPopper, Users, ClipboardCheck, Clock, ArrowRight, TrendingUp } from "lucide-react";

const isThisMonth = (iso) => iso && new Date(iso).getMonth() === new Date().getMonth();
const isBirthdayToday = (iso) => {
  if (!iso) return false;
  const d = new Date(iso), t = new Date();
  return d.getMonth() === t.getMonth() && d.getDate() === t.getDate();
};

function Stat({ icon: Icon, label, value, accent }) {
  return (
    <div className={`rounded-3xl p-6 ${accent ? "bg-vault-accent text-vault-panel" : "bg-vault-panel text-vault-cream"}`}>
      <div className="flex items-center justify-between">
        <div className={`text-xs font-bold uppercase tracking-widest ${accent ? "text-vault-panel/70" : "text-vault-accent"}`}>{label}</div>
        <Icon className={`h-5 w-5 ${accent ? "text-vault-panel/80" : "text-vault-subtle"}`} />
      </div>
      <div className="font-display text-4xl font-extrabold mt-3">{value}</div>
    </div>
  );
}

function Panel({ title, action, children }) {
  return (
    <div className="rounded-3xl bg-vault-panel p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-display text-lg font-bold text-vault-cream">{title}</h3>
        {action}
      </div>
      {children}
    </div>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [reqs, setReqs] = useState([]);
  const [gems, setGems] = useState([]);
  const [users, setUsers] = useState([]);
  const [notifs, setNotifs] = useState([]);

  useEffect(() => {
    getRequests().then(setReqs).catch(() => {});
    getNotifications().then((n) => setNotifs(n.slice(0, 5))).catch(() => {});
    if (user.role === "Employee") getGems().then(setGems).catch(() => {});
    if (user.role !== "Employee") getUsers().then(setUsers).catch(() => {});
  }, [user.role]);
  // getRequests/getGems/getUsers/getNotifications are stable module imports.

  const birthdayToday = isBirthdayToday(user.birthdate);
  const birthdayMonth = isThisMonth(user.birthdate);
  const annivMonth = isThisMonth(user.startDate);
  const years = new Date().getFullYear() - new Date(user.startDate).getFullYear();

  const allForCounts = reqs; // already scoped by backend
  const pendingHR = allForCounts.filter((r) => ["Submitted", "Manager Reviewed"].includes(r.currentStatus));
  const pendingDir = allForCounts.filter((r) => r.currentStatus === "HR Approved");
  const managerPending = allForCounts.filter((r) => r.currentStatus === "Submitted");

  const requestsPanelTitle =
    user.role === "Employee" ? "My Requests"
    : user.role === "Manager" ? `${user.primaryLocation} Requests`
    : "Approval Queue";

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-vault-panel/50">{user.role} Dashboard</div>
          <h1 className="font-display text-3xl sm:text-4xl font-extrabold text-vault-panel mt-1">Welcome, {user.firstName}</h1>
        </div>
        {(user.role === "Employee" || user.role === "Director" || user.role === "HR") && (
          <button onClick={() => navigate("/app/request/new")}
            className="inline-flex items-center gap-2 rounded-2xl bg-vault-panel text-vault-cream font-semibold px-5 py-3 hover:bg-vault-panel2 transition-colors">
            <FilePlus2 className="h-4 w-4" /> Submit Request
          </button>
        )}
      </div>

      {(birthdayToday || birthdayMonth || annivMonth) && (
        <div className="flex flex-wrap gap-3">
          {(birthdayToday || birthdayMonth) && (
            <div className="flex items-center gap-3 rounded-2xl bg-vault-accent text-vault-panel px-5 py-3 font-semibold">
              <Cake className="h-5 w-5" /> {birthdayToday ? "Happy Birthday! 50 gems added \ud83c\udf89" : "Your birthday is this month \u2014 50 gems incoming!"}
            </div>
          )}
          {annivMonth && (
            <div className="flex items-center gap-3 rounded-2xl bg-vault-panel text-vault-cream px-5 py-3 font-semibold">
              <PartyPopper className="h-5 w-5 text-vault-accent" /> {years}-year work anniversary this month — 50 gems!
            </div>
          )}
        </div>
      )}

      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {user.role === "Employee" && <>
          <Stat icon={Gem} label="Gems Balance" value={user.gemsBalance} accent />
          <Stat icon={ClipboardCheck} label="My Requests" value={reqs.length} />
          <Stat icon={Clock} label="In Review" value={reqs.filter(r => !["Completed","Director Approved","HR Denied","Director Denied","Draft"].includes(r.currentStatus)).length} />
          <Stat icon={TrendingUp} label="Approved" value={reqs.filter(r => ["Director Approved","Completed","HR Approved"].includes(r.currentStatus)).length} />
        </>}
        {user.role === "Manager" && <>
          <Stat icon={Users} label={`${user.primaryLocation} Team`} value={users.length} accent />
          <Stat icon={ClipboardCheck} label="Location Requests" value={reqs.length} />
          <Stat icon={Clock} label="Pending My Review" value={managerPending.length} />
          <Stat icon={Gem} label="My Gems" value={user.gemsBalance} />
        </>}
        {user.role === "HR" && <>
          <Stat icon={Clock} label="Pending HR Review" value={pendingHR.length} accent />
          <Stat icon={ClipboardCheck} label="All Requests" value={reqs.length} />
          <Stat icon={Users} label="All Users" value={users.length} />
          <Stat icon={Gem} label="My Gems" value={user.gemsBalance} />
        </>}
        {user.role === "Director" && <>
          <Stat icon={ClipboardCheck} label="Final Approval Queue" value={pendingDir.length} accent />
          <Stat icon={Clock} label="Pending HR Review" value={pendingHR.length} />
          <Stat icon={Users} label="All Users" value={users.length} />
          <Stat icon={ClipboardCheck} label="All Requests" value={reqs.length} />
        </>}
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2">
          <Panel title={requestsPanelTitle}
            action={<button onClick={() => navigate("/app/requests")} className="text-sm text-vault-accent font-semibold inline-flex items-center gap-1 hover:underline">View all <ArrowRight className="h-3.5 w-3.5" /></button>}>
            {reqs.length === 0 ? (
              <p className="text-sm text-vault-subtle py-8 text-center">No requests yet.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-vault-subtle text-xs uppercase tracking-wider">
                      <th className="py-2 font-semibold">Title</th>
                      {user.role !== "Employee" && <th className="py-2 font-semibold">Employee</th>}
                      <th className="py-2 font-semibold">Type</th>
                      <th className="py-2 font-semibold">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {reqs.slice(0, 6).map((r) => (
                      <tr key={r.id} onClick={() => navigate(`/app/requests/${r.id}`)}
                        className="border-t border-white/5 cursor-pointer hover:bg-vault-panel2/50">
                        <td className="py-3 pr-3 text-vault-cream font-medium">{r.requestTitle}</td>
                        {user.role !== "Employee" && <td className="py-3 pr-3 text-vault-subtle">{r.employeeName}</td>}
                        <td className="py-3 pr-3 text-vault-subtle">{r.requestType}</td>
                        <td className="py-3"><StatusBadge status={r.currentStatus} /></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Panel>
        </div>

        <div className="space-y-6">
          {user.role === "Employee" && (
            <Panel title="My Gems History" action={<button onClick={() => navigate("/app/gems")} className="text-sm text-vault-accent font-semibold hover:underline">All</button>}>
              <div className="space-y-3">
                {gems.slice(0, 4).map((g) => (
                  <div key={g.id} className="flex items-center justify-between">
                    <div>
                      <div className="text-sm text-vault-cream font-medium">{g.transactionType}</div>
                      <div className="text-xs text-vault-subtle truncate max-w-[140px]">{g.reason}</div>
                    </div>
                    <div className={`font-display font-bold ${g.amount >= 0 ? "text-vault-accent" : "text-red-400"}`}>{g.amount >= 0 ? "+" : ""}{g.amount}</div>
                  </div>
                ))}
              </div>
            </Panel>
          )}
          <Panel title="Recent Notifications" action={<button onClick={() => navigate("/app/notifications")} className="text-sm text-vault-accent font-semibold hover:underline">All</button>}>
            {notifs.length === 0 ? <p className="text-sm text-vault-subtle">You're all caught up.</p> : (
              <div className="space-y-3">
                {notifs.map((n) => (
                  <div key={n.id} className="flex items-start gap-3">
                    <div className={`mt-1.5 h-2 w-2 rounded-full shrink-0 ${n.isRead ? "bg-vault-subtle/40" : "bg-vault-accent"}`} />
                    <div>
                      <div className="text-sm text-vault-cream font-medium">{n.title}</div>
                      <div className="text-xs text-vault-subtle">{n.message}</div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Panel>
        </div>
      </div>
    </div>
  );
}
